import { useDispatch, useSelector } from "react-redux";
import useDeviceInfo from "./useDeviceInfo";
import { useCallback, useEffect, useRef, useState } from "react";
import { getS3BucketSessionCredentials, setS3BucketSessionCredentials } from "../DeviceServiceSlice";
import { GetObjectCommand, ListObjectsV2Command, S3Client } from "@aws-sdk/client-s3";
import moment from "moment";
import piexif from "piexifjs";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { base64ToBlob, blobToBase64 } from "src/helper/utils";

export default function useDeviceCapturedMedia (deviceId, path = '', withPublicUrl = false) {
  const deviceInfo = useDeviceInfo(deviceId);
  const bucketInfo = deviceInfo?.details?.defaultDockBucket;
  const { name: bucketName, region: bucketRegion } = bucketInfo || {};
  const bucketId = bucketInfo?.Id || bucketInfo?.id;
  const s3CredentialsRequest = useSelector(state => state.deviceService.s3BucketSessionCredentialRequest);
  const s3Credentials = useSelector(state => state.deviceService.s3BucketSessionCredentials?.[bucketId]?.credential);
  const s3Client = useRef(null);
  const dispatch = useDispatch();
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isFetchingFile, setIsFetchingFile] = useState(false);
  const isLoading = isLoadingCredentials || isLoadingFiles || isFetchingFile;
  const [mediaListLoaded, setMediaListLoaded] = useState(false);
  const [mediaList, setMediaList] = useState();
  const [currDir, setCurrDir] = useState(path);

  const getFileNameInfo = (fileName) => {
    const pathParts = fileName.split('/');
    const name = pathParts.pop();
    const path = pathParts.join('/');
    const nameParts = name.split('.');
    const extension = nameParts.pop();
    const nameInfoParts = nameParts[0].split('_');
    const timestamp = moment(nameInfoParts[1], 'YYYYMMDDHHmmss').unix();

    return {
      name,
      path,
      extension,
      timestamp,
      raw: fileName,
      dateString: nameInfoParts[1],
      number: nameInfoParts[2],
      contentTag: nameInfoParts[3],
    }
  }

  const getBucketCredentials = useCallback(() => {
    if(!s3Credentials || Date.now() - s3Credentials.requestTimestamp > 1800000) {
      console.log('📂 Getting S3 bucket session credentials');
      setIsLoadingCredentials(true);
      dispatch(getS3BucketSessionCredentials(bucketInfo?.Id || bucketInfo?.id));

      return false;
    }

    return true;
  }, [bucketInfo, dispatch, s3Credentials]);

  const getFilesList = (dir = currDir, getPublicUrl = false) => {
    if(!s3Client.current || !getBucketCredentials()) return;
    
    setIsLoadingFiles(true);
    dir = dir && !dir.endsWith('/') ? dir + '/' : dir;
    
    const command = new ListObjectsV2Command({
      Bucket: bucketName,
      Prefix: dir,
    });
    
    s3Client.current
      .send(command)
      .then((data) => {
        if(data.Contents) {
          const list = data.Contents.filter(item => item.Key !== dir);

          const filesWithNameInfo = list.filter(item => !item.Key.endsWith('/'))
            .map(item => ({ ...item, nameInfo: getFileNameInfo(item.Key) }));

          if(getPublicUrl) {
            (async () => {
              if(filesWithNameInfo) {
                const newMediaList = await Promise.all(filesWithNameInfo.map(async media => {
                  const url = await getObjectUrl(media.Key);
                  return { ...media, url };
                }));
        
                setMediaList(newMediaList);
                setMediaListLoaded(true);
                setIsLoadingFiles(false);
              }
            })();
          } else {
            setMediaList(filesWithNameInfo);
            setMediaListLoaded(true);
            setIsLoadingFiles(false);
          }
        } else {
          setMediaList([]);
          setMediaListLoaded(true);
          setIsLoadingFiles(false);
        }

        console.log('📂 Media list from s3 bucket', data);
      })
      .catch((error) => {
        console.error('📂 Media list from s3 bucket error', error);
      })
      .finally(() => {
        setIsLoadingFiles(false);
      });
  }

  const getObjectUrl = async (key, withDownalodHeader = false) => {
    if(!key || !s3Client.current) return;

    try {
      const command = new GetObjectCommand({
        Bucket: bucketName,
        Key: key,
        ...(withDownalodHeader ? { ResponseContentDisposition: `attachment; filename="${key.split('/').pop()}"` } : {}),
      });
  
      const url = await getSignedUrl(s3Client.current, command, { expiresIn: 3600 });
      return url;
    } catch (error) {
      console.error('📂 Error generating URL:', error);
      return null;
    }
  }

  const fetchMediaFile = async (key) => {
    console.log('📂 Download media file', key);
    if(!s3Client.current) return;

    try {
      const url = await getObjectUrl(key);

      if(url) {
        const response = await fetch(url);
        const blob = await response.blob();
        return blob;
      }

      return null;
    } catch (error) {
      console.error('📂 Error downloading file:', error);
    }
  }

  const downloadUrl = (url, filename) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
  
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  const getFileLocalDate = (file) => {
    return momentTimezone.tz(file.nameInfo.dateString, 'YYYYMMDDHHmmss', 'Asia/Shanghai').local();
  }

  const modifyExifDateTime = async (imageBlob, newDateTime) => {
    const base64Image = await blobToBase64(imageBlob);
    const exifObj = piexif.load(base64Image);
    const formattedDate = newDateTime.format("YYYY:MM:DD HH:mm:ss");
  
    exifObj["Exif"][piexif.ExifIFD.DateTimeOriginal] = formattedDate;
    exifObj["Exif"][piexif.ExifIFD.DateTimeDigitized] = formattedDate;
  
    const newExif = piexif.dump(exifObj);
    const updatedBase64Image = piexif.insert(newExif, base64Image);
  
    return base64ToBlob(updatedBase64Image);
  }

  const fetchAndSaveMediaFile = async (key) => {
    if(!key) return;

    setMessage('Downloading media file...');
    setIsFetchingFile(true);

    const blob = await fetchMediaFile(key)
    
    if(blob && ['jpg', 'jpeg'].includes(key.split('.').pop())) {
      const media = mediaList?.find(item => item.Key === key);
      const newDateTime = getFileLocalDate(media);
      const updatedBlob = await modifyExifDateTime(blob, newDateTime);
      const nameParts = media.nameInfo.name.split('_');

      nameParts[1] = newDateTime.format('YYYYMMDDHHmmss');
      const newName = nameParts.join('_');

      saveAs(updatedBlob, newName);
    } else if(blob) {
      saveAs(blob, key.split('/').pop());
    } else {
      console.error('📂 Failed to download file:', key);
    }

    setMessage(null);
    setIsFetchingFile(false);
  }

  const openFileInNewTab = async (key) => {
    const url = await getObjectUrl(key);
    if (url) {
      window.open(url, '_blank');
    } else {
      console.error('📂 Failed to generate URL for key:', key);
    }
  }

  const downloadMediaFileByUrl = (key) => {
    if(!key) return;

    setMessage('Starting file download...');
    setIsFetchingFile(true);

    getObjectUrl(key, true).then(url => {
      downloadUrl(url, key.split('/').pop());
      setTimeout(() => {
        setMessage(null);
        setIsFetchingFile(false);
      }, 1000);
    });
  }

  useEffect(() => {
    if (!s3CredentialsRequest) return;

    if(s3CredentialsRequest.status === 'success') {
      dispatch(setS3BucketSessionCredentials({ bucketId, data: {
        ...s3CredentialsRequest.data,
        requestTimestamp: Date.now(),
      }}));
    }
    
    setIsLoadingCredentials(false);
  }, [s3CredentialsRequest]);

  useEffect(() => {
    if (!s3Credentials) return;
    console.log('📂 S3 credentials received', s3Credentials);

    // Get media list
    const clientConfig = { 
      region: bucketRegion,
      credentials: {
        accessKeyId: s3Credentials.access_key_id,
        secretAccessKey: s3Credentials.access_key_secret,
        sessionToken: s3Credentials.security_token,
      }
    }

    s3Client.current = new S3Client(clientConfig);
    
    setTimeout(() => {
      getFilesList(undefined, withPublicUrl);
    }, 500);
  }, [s3Credentials]);

  useEffect(() => {
    if (path !== currDir) {
      setCurrDir(path);

      if(bucketId) {
        setMediaList(undefined);
        getFilesList(path, withPublicUrl);
      } else {
        setMediaList([]);
      }
    }
  }, [path, currDir, bucketId]);

  useEffect(() => {
    if (!bucketId) return;
    getBucketCredentials();
  }, [bucketId]);

  return {
    bucketId,
    bucketName,
    bucketRegion,
    s3Client,
    isLoadingCredentials,
    isLoadingFiles,
    isFetchingFile,
    isLoading,
    mediaListLoaded,
    mediaList,
    getFileNameInfo,
    getFilesList,
    getObjectUrl,
    fetchMediaFile,
    downloadUrl,
    modifyExifDateTime,
    fetchAndSaveMediaFile,
    openFileInNewTab,
    downloadMediaFileByUrl,
  }
}